import { DateTime } from 'luxon';
import { FC, Fragment } from 'react';
import styled from 'styled-components';

import { Td } from '@/components/Table';
import Text from '@/components/Text';
import { InvoiceChangelogResource } from '@/types/resources';
import { invoiceStatusLabels } from '@/utils/data/payments';
import { InvoiceStatus } from '@/utils/enums';

const KeyTd = styled(Td)`
  text-transform: capitalize;
`;

const GrayBadge = styled(Text)`
  padding: 3px 6px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

export const ReceiverName = styled(GrayBadge)`
  white-space: nowrap;
`;

const OldValue = GrayBadge;

const NewValue = styled(Text)`
  padding: 3px 6px;
  border-radius: 4px;
  background: rgba(65, 54, 207, 0.04);
`;

const DateHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Holder = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  > * {
    flex-shrink: 0;
  }
`;

interface Props {
  changelog: InvoiceChangelogResource;
}

const InvoicesChangelogTableRow: FC<Props> = ({ changelog }) => {
  const fields = Object.keys(changelog.new ?? {});

  const parsedDate = changelog.changedAt
    ? DateTime.fromISO(changelog.changedAt)
    : null;

  const formattedDate = parsedDate ? (
    <>
      <Text $type="smallBold">{parsedDate.toFormat('dd/LL/yyyy')}</Text>
      <Text $type="extraSmall" $color="gray">
        {parsedDate.toFormat('t')}
      </Text>
    </>
  ) : (
    '-'
  );

  const formatValue = (key: string, value: unknown) => {
    if (key === 'status')
      return invoiceStatusLabels[value as InvoiceStatus] ?? String(value);
    if (key === 'gpm') return value ? 'Yes' : 'No';
    return String(value ?? '');
  };

  if (!fields.length) {
    return (
      <tr>
        <Td>
          <Holder>
            {changelog.changedBy?.name ?? '-'} updated{' '}
            <ReceiverName $type="smallBold">
              {changelog.receiver ?? '-'}
            </ReceiverName>
          </Holder>
        </Td>
        <Td>-</Td>
        <Td>-</Td>
        <Td>
          <DateHolder>{formattedDate}</DateHolder>
        </Td>
      </tr>
    );
  }

  return (
    <>
      {fields.map((key) => (
        <Fragment key={key}>
          <tr>
            <Td>
              <Holder>
                {changelog.changedBy?.name ?? '-'} updated{' '}
                <ReceiverName $type="smallBold">
                  {changelog.receiver ?? '-'}
                </ReceiverName>
              </Holder>
            </Td>
            <KeyTd>{key.replace(/_/g, ' ')}</KeyTd>
            <Td>
              <Holder>
                <OldValue $type="smallBold">
                  {changelog.old?.[key] != null && changelog.old?.[key] !== ''
                    ? formatValue(key, changelog.old?.[key])
                    : '-'}
                </OldValue>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M1.875 6H10.125"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.75 2.625L10.125 6L6.75 9.375"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <NewValue $type="smallBold" $color="purple">
                  {formatValue(key, changelog.new?.[key])}
                </NewValue>
              </Holder>
            </Td>
            <Td>
              <DateHolder>{formattedDate}</DateHolder>
            </Td>
          </tr>
        </Fragment>
      ))}
    </>
  );
};

export default InvoicesChangelogTableRow;
